import { DropdownMenuContentProps } from '@radix-ui/react-dropdown-menu';
import { ReactNode } from 'react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '../ui/dropdown-menu';

/**
 * Props for the DropdownMenuNew component
 *
 * @remarks
 * Extends all DropdownMenuContentProps from Radix UI, allowing customization of:
 * - `align` (default: "end")
 * - `className` (default: "w-[224px]")
 * - Other content props like `sideOffset`, `onCloseAutoFocus`, etc.
 *
 * @see https://www.radix-ui.com/primitives/docs/components/dropdown-menu#content for all available props
 */
type DropdownMenuNewProps = {
  /**
   * The trigger element that opens the dropdown menu when clicked.
   * Typically a Button component with an icon.
   *
   * @example
   * ```tsx
   * <Button variant="ghost" size="icon">
   *   <MoreVertical className="h-4 w-4" />
   * </Button>
   * ```
   */
  trigger: ReactNode;

  /**
   * The menu items to display in the dropdown.
   * Use DropdownMenuGroup and DropdownMenuItem components to build the menu structure.
   *
   * @example
   * ```tsx
   * <DropdownMenuGroup>
   *   <DropdownMenuItem onSelect={() => console.log('Selected')}>
   *     <Inline space={2}>
   *       <FileText className="h-4 w-4 text-muted-foreground" />
   *       <p className="text-sm">Menu item</p>
   *     </Inline>
   *   </DropdownMenuItem>
   * </DropdownMenuGroup>
   * ```
   */
  children: ReactNode;
} & DropdownMenuContentProps;

/**
 * A dropdown menu component with a default width of 224px and right-aligned positioning.
 * Wraps Radix UI DropdownMenu primitives for a consistent menu experience.
 *
 * @remarks
 * Default props can be overridden:
 * - `align="end"` - Can be changed to "start" or "center"
 * - `className="w-[224px]"` - Can be overridden for custom widths
 * - All other DropdownMenuContentProps are supported (sideOffset, onCloseAutoFocus, etc.)
 *
 * @example
 * Basic usage:
 * ```tsx
 * <DropdownMenuNew
 *   trigger={
 *     <Button variant="ghost" size="icon">
 *       <MoreVertical className="h-4 w-4" />
 *     </Button>
 *   }
 * >
 *   <DropdownMenuGroup>
 *     <DropdownMenuItem onSelect={() => alert('Action')}>
 *       <Inline space={2}>
 *         <FileText className="h-4 w-4 text-muted-foreground" />
 *         <p className="text-sm">Key investor information</p>
 *       </Inline>
 *     </DropdownMenuItem>
 *   </DropdownMenuGroup>
 * </DropdownMenuNew>
 * ```
 *
 * @example
 * With custom width and alignment:
 * ```tsx
 * <DropdownMenuNew
 *   trigger={<Button>Menu</Button>}
 *   align="start"
 *   className="w-[300px]"
 * >
 *   <DropdownMenuGroup>
 *     <DropdownMenuItem>Wide menu item</DropdownMenuItem>
 *   </DropdownMenuGroup>
 * </DropdownMenuNew>
 * ```
 *
 * @param props - Component props
 * @returns A dropdown menu with trigger and content
 */
export const DropdownMenuNew = ({ trigger, children, ...rest }: DropdownMenuNewProps) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-[224px]" {...rest}>
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
